import { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Table from '@mui/joy/Table'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Checkbox from '@mui/joy/Checkbox'
import CircularProgress from '@mui/joy/CircularProgress'
import IconButton from '@mui/joy/IconButton'
import Tooltip from '@mui/joy/Tooltip'
import RefreshIcon from '@mui/icons-material/Refresh'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import ReplayIcon from '@mui/icons-material/Replay'

import { adminQueue, forceCleanup, requeueCall, timestampToDate, type BotStatus, type BotTriggerSource, type DsGmeetBot } from '../../api/dsGmeetApi'
import { StatusPill } from './StatusPill'
import { useCurrentUserId } from '../../hooks/useCurrentUserId'
import { CallDetail } from './CallDetail'

const STATUS_OPTIONS: Array<BotStatus | 'all'> = ['all', 'PENDING', 'PROCESSED', 'UNATTRIBUTED', 'FAILED', 'CREATED', 'RECORDING', 'DONE', 'CANCELLED']
const TRIGGER_OPTIONS: Array<BotTriggerSource | 'all'> = ['all', 'MANUAL', 'CALENDAR']

function fmtRelative(d: Date | null): string {
    if (!d) return '—'
    const diff = Date.now() - d.getTime()
    const mins = Math.floor(diff / 60_000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    return `${days}d ago`
}

interface Props {
    selectedBotId: string | null
    onSelect: (botId: string) => void
    refreshNonce: number
}

export function QueueTab({ selectedBotId, onSelect, refreshNonce }: Props) {
    const userId = useCurrentUserId()
    const [status, setStatus] = useState<BotStatus | 'all'>('all')
    const [trigger, setTrigger] = useState<BotTriggerSource | 'all'>('all')
    const [items, setItems] = useState<DsGmeetBot[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [selected, setSelected] = useState<Record<string, boolean>>({})
    const [bulkBusy, setBulkBusy] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const res = await adminQueue({
                status: status === 'all' ? undefined : [status],
                triggerSource: trigger === 'all' ? undefined : trigger,
                limit: 200,
            })
            setItems(res)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }, [status, trigger])

    useEffect(() => {
        void load()
    }, [load, refreshNonce])

    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected])

    const bulkRequeue = async () => {
        setBulkBusy(true)
        try {
            for (const id of selectedIds) await requeueCall(id, userId).catch(() => undefined)
            setSelected({})
            await load()
        } finally {
            setBulkBusy(false)
        }
    }

    const bulkCleanup = async () => {
        if (!window.confirm(`Force cleanup ${selectedIds.length} bot(s)? Recall media will be permanently deleted.`)) return
        setBulkBusy(true)
        try {
            for (const id of selectedIds) await forceCleanup(id).catch(() => undefined)
            setSelected({})
            await load()
        } finally {
            setBulkBusy(false)
        }
    }

    const counts = useMemo(() => {
        const map: Partial<Record<BotStatus, number>> = {}
        for (const b of items) map[b.status] = (map[b.status] ?? 0) + 1
        return map
    }, [items])

    const detailOpen = !!selectedBotId

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: detailOpen ? { xs: '1fr', lg: '1.2fr 1fr' } : '1fr', gap: 2, minHeight: 0 }}>
            <Stack gap={1.5} sx={{ minWidth: 0 }}>
                {/* Filters + bulk actions */}
                <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                    <Select size="sm" value={status} onChange={(_, v) => setStatus((v as BotStatus | 'all') ?? 'all')} sx={{ minWidth: 160 }}>
                        {STATUS_OPTIONS.map((s) => (
                            <Option key={s} value={s}>
                                {s === 'all' ? 'All statuses' : s}
                            </Option>
                        ))}
                    </Select>
                    <Select size="sm" value={trigger} onChange={(_, v) => setTrigger((v as BotTriggerSource | 'all') ?? 'all')} sx={{ minWidth: 140 }}>
                        {TRIGGER_OPTIONS.map((s) => (
                            <Option key={s} value={s}>
                                {s === 'all' ? 'All triggers' : s}
                            </Option>
                        ))}
                    </Select>

                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                        <Chip size="sm" variant="soft">
                            {items.length} total
                        </Chip>
                        {counts.PENDING ? (
                            <Chip size="sm" variant="soft" color="warning">
                                {counts.PENDING} pending
                            </Chip>
                        ) : null}
                        {counts.PROCESSED ? (
                            <Chip size="sm" variant="soft" color="success">
                                {counts.PROCESSED} processed
                            </Chip>
                        ) : null}
                        {counts.FAILED ? (
                            <Chip size="sm" variant="soft" color="danger">
                                {counts.FAILED} failed
                            </Chip>
                        ) : null}
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Button size="sm" variant="outlined" startDecorator={<ReplayIcon />} disabled={selectedIds.length === 0 || bulkBusy} onClick={bulkRequeue}>
                        Re-queue {selectedIds.length || ''}
                    </Button>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        startDecorator={<CleaningServicesIcon />}
                        disabled={selectedIds.length === 0 || bulkBusy}
                        onClick={bulkCleanup}
                    >
                        Cleanup {selectedIds.length || ''}
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton size="sm" variant="plain" onClick={load} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {error && (
                    <Typography color="danger" level="body-sm">
                        {error}
                    </Typography>
                )}

                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: { xs: 'none', lg: '60vh' } }}>
                    <Table stickyHeader hoverRow size="sm">
                        <thead>
                            <tr>
                                <th style={{ width: 36 }} />
                                <th>Bot</th>
                                <th style={{ width: 130 }}>Status</th>
                                <th style={{ width: 90 }}>Trigger</th>
                                <th style={{ width: 110 }}>Created</th>
                                <th style={{ width: 100 }}>Ack</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((b) => {
                                const isSelected = b.recall_bot_id === selectedBotId
                                return (
                                    <tr
                                        key={b.recall_bot_id}
                                        style={{ cursor: 'pointer', background: isSelected ? 'var(--joy-palette-primary-softBg)' : undefined }}
                                        onClick={() => onSelect(b.recall_bot_id)}
                                    >
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                size="sm"
                                                checked={!!selected[b.recall_bot_id]}
                                                onChange={(e) => setSelected((prev) => ({ ...prev, [b.recall_bot_id]: e.target.checked }))}
                                            />
                                        </td>
                                        <td>
                                            <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                {b.recall_bot_id}
                                            </Typography>
                                        </td>
                                        <td>
                                            <StatusPill status={b.status} />
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="outlined">
                                                {b.trigger_source}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Typography level="body-xs" color="neutral">
                                                {fmtRelative(timestampToDate(b.created_at))}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs" color="neutral">
                                                {b.ack_outcome ?? '—'}
                                            </Typography>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                    {loading && (
                        <Stack alignItems="center" sx={{ p: 2 }}>
                            <CircularProgress size="sm" />
                        </Stack>
                    )}
                    {!loading && items.length === 0 && (
                        <Stack alignItems="center" sx={{ p: 3 }} gap={0.5}>
                            <Typography level="body-sm" color="neutral">
                                No bots match the current filters.
                            </Typography>
                            <Typography level="body-xs" color="neutral">
                                Use the &quot;Record this call&quot; button to start one manually, or connect a calendar to auto-record.
                            </Typography>
                        </Stack>
                    )}
                </Sheet>
            </Stack>

            {detailOpen && selectedBotId && (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden', height: '100%', minHeight: 0 }}>
                    <CallDetail botId={selectedBotId} onClose={() => onSelect(selectedBotId)} onChanged={load} />
                </Sheet>
            )}
        </Box>
    )
}
